import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  MapPin, 
  RefreshCw, 
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Download,
  TrendingUp
} from 'lucide-react';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { getDateRange } from '../lib/google-ads-client';

interface ServiceAreaPage {
  id: string;
  company_id: string;
  brand_key: string;
  geo_id: string;
  topic_id: string;
  wp_post_id: string;
  page_url: string;
  title: string;
  slug: string;
  word_count: number;
  quality_score: number;
  uniqueness_score: number;
  indexability: 'index' | 'noindex';
  status: 'published' | 'draft' | 'scheduled' | 'deleted';
  published_at: string;
  updated_at: string;
  geo_name?: string;
  topic_name?: string;
}

interface QueueStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

interface GeoPerformance {
  location: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  conversionRate: number;
  roas: number;
  avgCpc: number;
}

export default function ServiceAreaHubsPage() {
  const [pages, setPages] = useState<ServiceAreaPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [geoPerformance, setGeoPerformance] = useState<GeoPerformance[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('published');
  const [indexabilityFilter, setIndexabilityFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [qualityMinFilter, setQualityMinFilter] = useState('');
  const [uniquenessMinFilter, setUniquenessMinFilter] = useState('');
  
  const pageSize = 25;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch pages
  const fetchPages = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('wp_service_area_pages')
        .select(`
          *,
          geo_units!wp_service_area_pages_geo_id_fkey(name),
          service_topics!wp_service_area_pages_topic_id_fkey(display_name)
        `, { count: 'exact' })
        .order('published_at', { ascending: false })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      // Apply filters
      if (statusFilter) query = query.eq('status', statusFilter);
      if (indexabilityFilter) query = query.eq('indexability', indexabilityFilter);
      if (companyFilter) query = query.eq('company_id', companyFilter);
      if (searchFilter) {
        query = query.or(`title.ilike.%${searchFilter}%,slug.ilike.%${searchFilter}%`);
      }
      if (qualityMinFilter) query = query.gte('quality_score', parseInt(qualityMinFilter));
      if (uniquenessMinFilter) query = query.gte('uniqueness_score', parseInt(uniquenessMinFilter));

      const { data, error, count } = await query;
      if (error) throw error;

      const mappedPages = (data || []).map((page: any) => ({
        ...page,
        geo_name: page.geo_units?.name,
        topic_name: page.service_topics?.display_name
      }));

      setPages(mappedPages);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch queue statistics
  const fetchQueueStats = async () => {
    try {
      const { data, error } = await supabase.from('page_build_queue').select('status');
      if (error) throw error;

      const stats = (data || []).reduce((acc: QueueStats, item) => {
        acc[item.status as keyof QueueStats] = (acc[item.status as keyof QueueStats] || 0) + 1;
        return acc;
      }, { pending: 0, processing: 0, completed: 0, failed: 0 });

      setQueueStats(stats);
    } catch (error) {
      console.error('Error fetching queue stats:', error);
    }
  };

  // Fetch Google Ads geo-performance data
  const loadGoogleAdsGeoPerformance = async () => {
    try {
      setAdsLoading(true);
      const range = getDateRange(30);
      
      const { data, error } = await supabase
        .from('google_ads_campaign_performance')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate);

      if (error) throw error;

      // Group by location and aggregate metrics
      const locationMap = new Map<string, {
        impressions: number;
        clicks: number;
        cost: number;
        conversions: number;
        conversionValue: number;
      }>();

      (data || []).forEach((row: any) => {
        const location = row.campaign_name?.split('-')[0]?.trim() || 'Unknown';
        
        const existing = locationMap.get(location) || {
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionValue: 0,
        };

        locationMap.set(location, {
          impressions: existing.impressions + (row.impressions || 0),
          clicks: existing.clicks + (row.clicks || 0),
          cost: existing.cost + (row.cost || 0),
          conversions: existing.conversions + (row.conversions || 0),
          conversionValue: existing.conversionValue + (row.conversion_value || 0),
        });
      });

      // Calculate derived metrics
      const performanceData: GeoPerformance[] = Array.from(locationMap.entries()).map(([location, metrics]) => {
        const ctr = metrics.impressions > 0 ? (metrics.clicks / metrics.impressions) * 100 : 0;
        const conversionRate = metrics.clicks > 0 ? (metrics.conversions / metrics.clicks) * 100 : 0;
        const roas = metrics.cost > 0 ? (metrics.conversionValue / metrics.cost) : 0;
        const avgCpc = metrics.clicks > 0 ? metrics.cost / metrics.clicks : 0;

        return {
          location,
          impressions: metrics.impressions,
          clicks: metrics.clicks,
          cost: metrics.cost,
          conversions: metrics.conversions,
          ctr: parseFloat(ctr.toFixed(2)),
          conversionRate: parseFloat(conversionRate.toFixed(2)),
          roas: parseFloat(roas.toFixed(2)),
          avgCpc: parseFloat(avgCpc.toFixed(2)),
        };
      });

      performanceData.sort((a, b) => b.cost - a.cost);
      setGeoPerformance(performanceData);
    } catch (error) {
      console.error('Error loading Google Ads geo-performance:', error);
    } finally {
      setAdsLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
    fetchQueueStats();
    loadGoogleAdsGeoPerformance();
  }, [currentPage, statusFilter, indexabilityFilter, companyFilter, searchFilter, qualityMinFilter, uniquenessMinFilter]);

  const toggleSelection = (id: string) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedItems(prev => 
      prev.length === pages.length ? [] : pages.map(p => p.id)
    );
  };

  const handleBulkRegenerate = async () => {
    if (!confirm(`Regenerate ${selectedItems.length} selected pages?`)) return;

    setProcessing(true);
    try {
      const selectedPages = pages.filter(p => selectedItems.includes(p.id));
      const queueItems = selectedPages.map(page => ({
        company_id: page.company_id,
        geo_id: page.geo_id,
        topic_id: page.topic_id,
        priority: 'high',
        status: 'pending'
      }));

      const { error } = await supabase.from('page_build_queue').insert(queueItems);
      if (error) throw error;

      alert(`${selectedItems.length} pages queued for regeneration`);
      setSelectedItems([]);
      fetchQueueStats();
    } catch (error) {
      console.error('Error regenerating pages:', error);
      alert('Error regenerating pages');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedItems.length} selected pages?`)) return;

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('wp_service_area_pages')
        .update({ status: 'deleted', updated_at: new Date().toISOString() })
        .in('id', selectedItems);

      if (error) throw error;

      alert(`${selectedItems.length} pages deleted`);
      setSelectedItems([]);
      fetchPages();
    } catch (error) {
      console.error('Error deleting pages:', error);
      alert('Error deleting pages');
    } finally {
      setProcessing(false);
    }
  };

  const triggerRegenerate = async (page: ServiceAreaPage) => {
    if (!confirm(`Regenerate "${page.title}"?`)) return;

    try {
      const { error } = await supabase.from('page_build_queue').insert({
        company_id: page.company_id,
        geo_id: page.geo_id,
        topic_id: page.topic_id,
        priority: 'high',
        status: 'pending'
      });

      if (error) throw error;
      alert('Page queued for regeneration');
      fetchQueueStats();
    } catch (error) {
      console.error('Error triggering regeneration:', error);
      alert('Error triggering regeneration');
    }
  };

  const QualityBadge = ({ score }: { score: number }) => {
    const color = score >= 90 ? 'green' : score >= 75 ? 'yellow' : score >= 60 ? 'orange' : 'red';
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium bg-${color}-100 text-${color}-800`}>
        {score}
      </span>
    );
  };

  const IndexabilityBadge = ({ indexability }: { indexability: string }) => {
    return indexability === 'index' ? (
      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
        <CheckCircle className="w-3 h-3" />
        Index
      </span>
    ) : (
      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 flex items-center gap-1">
        <XCircle className="w-3 h-3" />
        Noindex
      </span>
    );
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Area Hub Pages</h1>
          <p className="text-gray-600 mt-1">Manage programmatic SEO landing pages</p>
        </div>
      </div>

      {/* Queue Statistics */}
      {queueStats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{queueStats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Processing</p>
                <p className="text-2xl font-bold text-gray-900">{queueStats.processing}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{queueStats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-gray-900">{queueStats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="font-medium">Filters</h3>
        </div>
        <div className="grid grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Title or slug..."
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input">
              <option value="">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Indexability</label>
            <select value={indexabilityFilter} onChange={(e) => setIndexabilityFilter(e.target.value)} className="input">
              <option value="">All</option>
              <option value="index">Index</option>
              <option value="noindex">Noindex</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="input">
              <option value="">All</option>
              <option value="halesac">Hales AC</option>
              <option value="myccs">MyCCS</option>
              <option value="abrahamac">Abraham AC</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Quality</label>
            <input
              type="number"
              value={qualityMinFilter}
              onChange={(e) => setQualityMinFilter(e.target.value)}
              placeholder="0-100"
              min="0"
              max="100"
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Uniqueness</label>
            <input
              type="number"
              value={uniquenessMinFilter}
              onChange={(e) => setUniquenessMinFilter(e.target.value)}
              placeholder="0-100"
              min="0"
              max="100"
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedItems.length} selected
          </span>
          <div className="flex gap-2">
            <button onClick={handleBulkRegenerate} className="btn-primary" disabled={processing}>
              Regenerate
            </button>
            <button onClick={handleBulkDelete} className="btn-danger" disabled={processing}>
              Delete
            </button>
            <button onClick={() => setSelectedItems([])} className="btn-secondary">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Pages Table */}
      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-12">
                <input
                  type="checkbox"
                  checked={selectedItems.length === pages.length && pages.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th>Page</th>
              <th>Location</th>
              <th>Service</th>
              <th>Quality</th>
              <th>Uniqueness</th>
              <th>Words</th>
              <th>Indexability</th>
              <th>Status</th>
              <th>Published</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((page) => (
              <tr key={page.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(page.id)}
                    onChange={() => toggleSelection(page.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td>
                  <div>
                    <p className="font-medium text-gray-900">{page.title}</p>
                    <p className="text-sm text-gray-500">{page.slug}</p>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>{page.geo_name}</span>
                  </div>
                </td>
                <td>{page.topic_name}</td>
                <td><QualityBadge score={page.quality_score} /></td>
                <td><QualityBadge score={page.uniqueness_score} /></td>
                <td>{page.word_count?.toLocaleString()}</td>
                <td><IndexabilityBadge indexability={page.indexability} /></td>
                <td>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    page.status === 'published' ? 'bg-green-100 text-green-800' :
                    page.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    page.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {page.status}
                  </span>
                </td>
                <td className="text-sm text-gray-500">
                  {new Date(page.published_at).toLocaleDateString()}
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <a
                      href={page.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                      title="View page"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => triggerRegenerate(page)}
                      className="text-gray-600 hover:text-gray-800"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} pages
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-4 py-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="card p-6">
        <h3 className="font-medium mb-4">Summary Statistics</h3>
        <div className="grid grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-600">Total Pages</p>
            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Avg Quality Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {pages.length > 0 
                ? Math.round(pages.reduce((sum, p) => sum + p.quality_score, 0) / pages.length)
                : 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Avg Uniqueness</p>
            <p className="text-2xl font-bold text-gray-900">
              {pages.length > 0
                ? Math.round(pages.reduce((sum, p) => sum + p.uniqueness_score, 0) / pages.length)
                : 0}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Indexable Pages</p>
            <p className="text-2xl font-bold text-gray-900">
              {pages.filter(p => p.indexability === 'index').length}
            </p>
          </div>
        </div>
      </div>

      {/* Paid Search Performance by Location */}
      <div className="card p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium">Paid Search Performance by Location</h3>
            <span className="text-sm text-gray-500">(Last 30 days)</span>
          </div>
          <a
            href="/google-ads"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            View Detailed Analysis
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {adsLoading ? (
          <div className="py-8 text-center text-gray-500">Loading Google Ads data...</div>
        ) : geoPerformance.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No Google Ads data available for the selected period
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th className="text-right">Impressions</th>
                  <th className="text-right">Clicks</th>
                  <th className="text-right">CTR</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">Avg CPC</th>
                  <th className="text-right">Conversions</th>
                  <th className="text-right">Conv. Rate</th>
                  <th className="text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {geoPerformance.map((geo) => (
                  <tr key={geo.location}>
                    <td>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{geo.location}</span>
                      </div>
                    </td>
                    <td className="text-right text-gray-900">{geo.impressions.toLocaleString()}</td>
                    <td className="text-right text-gray-900">{geo.clicks.toLocaleString()}</td>
                    <td className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        geo.ctr >= 5 ? 'bg-green-100 text-green-800' :
                        geo.ctr >= 3 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {geo.ctr}%
                      </span>
                    </td>
                    <td className="text-right font-medium text-gray-900">${geo.cost.toFixed(2)}</td>
                    <td className="text-right text-gray-600">${geo.avgCpc.toFixed(2)}</td>
                    <td className="text-right text-gray-900">{geo.conversions.toFixed(1)}</td>
                    <td className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        geo.conversionRate >= 5 ? 'bg-green-100 text-green-800' :
                        geo.conversionRate >= 3 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {geo.conversionRate}%
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        geo.roas >= 4 ? 'bg-green-100 text-green-800' :
                        geo.roas >= 2 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {geo.roas.toFixed(2)}x
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
